import { Bar } from 'react-chartjs-2';
import Paper from '@trackman/web-shared-components/components/Paper/Paper';
import { ChartData, ChartOptions } from 'chart.js';
import { NoData } from 'pages/Admin/Analytics/components/NoData/NoData';
import { SourceBadge } from 'pages/Admin/Analytics/components/SourceBadge/SourceBadge';
import { currencyFormatter, formatMonth } from 'pages/Admin/Analytics/utils/monthlySeries';
import { useGetCertRevenueBreakdown } from 'queryHooks/Admin/analytics';

const chartOptions: ChartOptions<'bar'> = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'bottom',
      labels: {
        boxWidth: 10,
        font: { size: 11 },
      },
    },
    tooltip: {
      callbacks: {
        label: (context) => `${context.dataset.label}: ${currencyFormatter.format(Number(context.raw ?? 0))}`,
      },
    },
  },
  scales: {
    x: {
      stacked: true,
      grid: { display: false },
      ticks: { font: { size: 11 } },
    },
    y: {
      stacked: true,
      grid: { color: 'rgba(0, 0, 0, 0.06)' },
      ticks: {
        font: { size: 11 },
        callback: (value) => currencyFormatter.format(Number(value)),
      },
    },
  },
};

export const CertRevenueBreakdownChart = () => {
  const { data, isLoading, isError } = useGetCertRevenueBreakdown();
  const hasData = Boolean(data?.length);

  const chartData: ChartData<'bar'> = {
    labels: data?.map((entry) => formatMonth(entry.year, entry.month)) ?? [],
    datasets: [
      {
        label: 'Full price',
        data: data?.map((entry) => entry.fullPriceIncome) ?? [],
        backgroundColor: '#475569',
        borderRadius: 3,
      },
      {
        label: 'Premium (50% off)',
        data: data?.map((entry) => entry.premiumIncome) ?? [],
        backgroundColor: 'rgba(71, 85, 105, 0.45)',
        borderRadius: 3,
      },
      {
        label: 'Other discounted',
        data: data?.map((entry) => entry.otherDiscountedIncome) ?? [],
        backgroundColor: 'rgba(59, 130, 246, 0.55)',
        borderRadius: 3,
      },
    ],
  };

  return (
    <Paper className='analytics-card analytics-chart-card'>
      <div className='analytics-card__title-group'>
        <span className='card-title'>Cert revenue breakdown</span>
        <SourceBadge source='RECURLY' />
      </div>
      <p className='analytics-card__subtitle'>Full-price, premium, and other discounted certification revenue by month.</p>

      {isLoading || isError || !hasData ? (
        <NoData loading={isLoading} error={isError} noData={!hasData} />
      ) : (
        <div className='analytics-chart-card__content'>
          <Bar data={chartData} options={chartOptions} />
        </div>
      )}
    </Paper>
  );
};

export default CertRevenueBreakdownChart;
